import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Nav, Footer, WhatsAppButton } from "@/components/Chrome";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SERVICE_PAGES } from "@/data/servicePages";

function Faq({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-white/10">
            <button
                onClick={() => setOpen(!open)}
                data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
                aria-expanded={open}
            >
                <span className="font-display text-lg font-bold tracking-tight text-white">{q}</span>
                <Plus size={18} className={`shrink-0 text-white/50 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
                        <p className="max-w-2xl pb-6 text-sm font-light leading-relaxed text-white/60">{a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function ServicePage() {
    const { slug } = useParams();
    const page = SERVICE_PAGES[slug];
    if (!page) return <Navigate to="/" replace />;

    const related = Object.keys(SERVICE_PAGES).filter((s) => s !== slug).slice(0, 3);
    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    };

    return (
        <div className="min-h-screen bg-base" data-testid={`service-page-${slug}`}>
            <SEO title={page.title} description={page.description} path={`/services/${slug}`} jsonLd={faqLd} />
            <Nav />
            <main className="pt-16">
                <section className="border-b border-white/10 py-24 md:py-32">
                    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">HIGHTS services</p>
                        <h1 className="mt-6 max-w-4xl font-display font-black tracking-tighter" data-testid="service-h1">
                            <MaskedLine delay={0.1}>
                                <span className="block text-4xl leading-[1.0] text-white sm:text-5xl lg:text-6xl">{page.h1}</span>
                            </MaskedLine>
                        </h1>
                        <Reveal delay={0.3}>
                            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg">{page.intro}</p>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4">
                                <Link to="/book" data-testid="service-book-button" className="bg-white px-8 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/85">
                                    Book a free consultation
                                </Link>
                                <Link to="/" data-testid="service-demos-link" className="border border-white/25 px-8 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-white/10">
                                    Experience the main site
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </section>

                <section className="py-20 md:py-28">
                    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">What HIGHTS delivers</p>
                            <ul className="mt-8 divide-y divide-white/10 border-y border-white/10">
                                {page.bullets.map((b, i) => (
                                    <Reveal key={b} delay={i * 0.06} y={12}>
                                        <li className="flex items-baseline gap-5 py-5">
                                            <span className="font-mono text-[10px] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                                            <span className="text-base font-light text-white/75 md:text-lg">{b}</span>
                                        </li>
                                    </Reveal>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">Questions, answered</p>
                            <div className="mt-8">
                                {page.faqs.map((f) => <Faq key={f.q} q={f.q} a={f.a} />)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="border-t border-white/10 bg-void py-20">
                    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/40">Related services</p>
                        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                            {related.map((s) => (
                                <Link key={s} to={`/services/${s}`} data-testid={`related-${s}`}
                                    className="group flex items-center justify-between border border-white/12 bg-surface p-6 transition-colors hover:border-white/35">
                                    <span className="font-display text-lg font-bold tracking-tight text-white">{SERVICE_PAGES[s].h1.split(",")[0].split("—")[0]}</span>
                                    <ArrowUpRight size={18} className="text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-white" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
            <WhatsAppButton />
        </div>
    );
}
